import { useState, useEffect } from 'react';
import { useWeeklyFolders } from '@/contexts/WeeklyFolderContext';
import { api } from '@/lib/api';
import styles from './DashboardPage.module.css';

interface Stats {
  documents: {
    total: number;
    processing: number;
    completed: number;
    error: number;
    pending: number;
    needs_review: number;
    permanent: { total: number; completed: number };
    weekly: { total: number; completed: number };
  };
  folders: {
    total: number;
    active: number;
    archived: number;
  };
}

export function DashboardPage() {
  const { selectedFolder, currentFolder, folders } = useWeeklyFolders();
  const [stats, setStats] = useState<Stats | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    async function loadStats() {
      setIsLoading(true);
      try {
        const [docsResponse, foldersResponse] = await Promise.all([
          api.get<Stats['documents']>('/documents/processing-stats'),
          api.get<Stats['folders']>('/weekly-folders/stats'),
        ]);

        setStats({
          documents: docsResponse.data || {
            total: 0, processing: 0, completed: 0, error: 0, pending: 0, needs_review: 0,
            permanent: { total: 0, completed: 0 },
            weekly: { total: 0, completed: 0 },
          },
          folders: foldersResponse.data || { total: 0, active: 0, archived: 0 },
        });
      } catch (error) {
        console.error('Failed to load stats:', error);
      } finally {
        setIsLoading(false);
      }
    }

    loadStats();
  }, []);

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1>Dashboard</h1>
        <p className={styles.subtitle}>Welcome to Market Commentary</p>
      </div>

      {/* Current Folder Info */}
      {selectedFolder && (
        <div className={styles.currentFolder}>
          <div className={styles.folderCard}>
            <div className={styles.folderHeader}>
              <h2>{selectedFolder.title}</h2>
              {selectedFolder.id === currentFolder?.id && (
                <span className="badge badge-success">Current Week</span>
              )}
            </div>
            <div className={styles.folderMeta}>
              <span>
                {formatDate(selectedFolder.week_start)} - {formatDate(selectedFolder.week_end)}
              </span>
            </div>
            <div className={styles.folderStats}>
              <div className={styles.folderStat}>
                <span className={styles.statValue}>{selectedFolder.document_count}</span>
                <span className={styles.statLabel}>Documents</span>
              </div>
              <div className={styles.folderStat}>
                <span className={styles.statValue}>{selectedFolder.commentary_count}</span>
                <span className={styles.statLabel}>Commentaries</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Stats Grid */}
      {isLoading ? (
        <div className={styles.loading}>
          <div className="spinner" />
          <span>Loading stats...</span>
        </div>
      ) : stats ? (
        <div className={styles.statsGrid}>
          <div className={styles.statCard}>
            <h3>Permanent Documents</h3>
            <div className={styles.statValue}>{stats.documents.permanent?.total || 0}</div>
            <div className={styles.statDetails}>
              <span className={styles.statDetailItem}>
                <span className={styles.dotSuccess} /> {stats.documents.permanent?.completed || 0} completed
              </span>
              <span className={styles.statDetailItem}>
                Reference documents available for all commentaries
              </span>
            </div>
          </div>

          <div className={styles.statCard}>
            <h3>Weekly Documents</h3>
            <div className={styles.statValue}>{stats.documents.weekly?.total || 0}</div>
            <div className={styles.statDetails}>
              <span className={styles.statDetailItem}>
                <span className={styles.dotSuccess} /> {stats.documents.weekly?.completed || 0} completed
              </span>
              <span className={styles.statDetailItem}>
                <span className={styles.dotWarning} /> {stats.documents.processing} processing
              </span>
              {stats.documents.error > 0 && (
                <span className={styles.statDetailItem}>
                  <span className={styles.dotError} /> {stats.documents.error} errors
                </span>
              )}
              {stats.documents.needs_review > 0 && (
                <span className={styles.statDetailItem}>
                  <span className={styles.dotWarning} /> {stats.documents.needs_review} needs review
                </span>
              )}
            </div>
          </div>

          <div className={styles.statCard}>
            <h3>Weekly Folders</h3>
            <div className={styles.statValue}>{stats.folders.total}</div>
            <div className={styles.statDetails}>
              <span className={styles.statDetailItem}>
                <span className={styles.dotSuccess} /> {stats.folders.active} active
              </span>
              <span className={styles.statDetailItem}>
                <span className={styles.dotMuted} /> {stats.folders.archived} archived
              </span>
            </div>
          </div>

          <div className={styles.statCard}>
            <h3>Recent Folders</h3>
            <div className={styles.recentFolders}>
              {folders.slice(0, 5).map((folder) => (
                <div key={folder.id} className={styles.recentFolder}>
                  <span className={styles.folderName}>{folder.title}</span>
                  <span className={`badge badge-${folder.status === 'active' ? 'success' : 'secondary'}`}>
                    {folder.status}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : null}

      {/* Quick Actions */}
      <div className={styles.quickActions}>
        <h2>Quick Actions</h2>
        <div className={styles.actionGrid}>
          <a href="/documents" className={styles.actionCard}>
            <span className={styles.actionIcon}>📄</span>
            <span className={styles.actionLabel}>Upload Documents</span>
          </a>
          <a href="/commentary" className={styles.actionCard}>
            <span className={styles.actionIcon}>✍️</span>
            <span className={styles.actionLabel}>Generate Commentary</span>
          </a>
          <a href="/templates" className={styles.actionCard}>
            <span className={styles.actionIcon}>📝</span>
            <span className={styles.actionLabel}>Manage Templates</span>
          </a>
          <a href="/feedback" className={styles.actionCard}>
            <span className={styles.actionIcon}>💬</span>
            <span className={styles.actionLabel}>Add Feedback</span>
          </a>
        </div>
      </div>
    </div>
  );
}
